// Family invitation accept page -- iteration 077
// URL: /family/accept/:token

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import * as api from "@/lib/api";
import type { FamilyAcceptPreview } from "@dugads/shared";

export default function FamilyAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState<FamilyAcceptPreview | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .getFamilyInvitePreview(token)
      .then((res) => {
        setPreview(res.preview);
        setInviteEmail(res.email);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Invalid or expired invitation");
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleAccept() {
    if (!token) return;
    setAccepting(true);
    setError("");
    try {
      await api.acceptFamilyInvite(token);
      setAccepted(true);
      setTimeout(() => navigate("/profile"), 1500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div data-page="family-accept" className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>Family Invitation</CardTitle>
          <CardDescription>
            {loading ? "Loading…" : preview ? `You've been invited as ${preview.inviter_label}.` : "Invitation details"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {preview && (
            <>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Family:</span>{" "}
                  <span className="font-medium">{preview.group_name}</span>
                </div>
                {preview.primary_display_name && (
                  <div>
                    <span className="text-muted-foreground">Invited by:</span>{" "}
                    <span className="font-medium">{preview.primary_display_name}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Children:</span>{" "}
                  <span className="font-medium">{preview.children_count}</span>
                </div>
                {inviteEmail && (
                  <div>
                    <span className="text-muted-foreground">Invite sent to:</span>{" "}
                    <span className="font-medium">{inviteEmail}</span>
                  </div>
                )}
              </div>

              {accepted ? (
                <p className="text-sm text-green-700">Accepted -- redirecting to your profile…</p>
              ) : !user ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Please sign in with {inviteEmail ?? "the invited email"} to accept.
                  </p>
                  <Link to={`/login?next=/family/accept/${token}`} className={buttonVariants()}>
                    Sign In
                  </Link>
                </div>
              ) : user.user.email.toLowerCase() !== (inviteEmail ?? "").toLowerCase() ? (
                <p className="text-sm text-destructive">
                  This invitation was sent to {inviteEmail}. Sign out and sign in with that address to accept.
                </p>
              ) : (
                <Button onClick={handleAccept} disabled={accepting} data-action="family-accept">
                  {accepting ? "Accepting…" : "Accept Invitation"}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
